import { useState, useEffect, useCallback } from 'react';
import znoozinuTokenService from '../services/znoozinuTokenService';

/**
 * Custom hook for managing ZNOOZINU token status and premium features
 * @param {string} walletAddress - User's wallet address
 * @returns {Object} Token status and utility functions
 */
export const useZnoozinuToken = (walletAddress) => {
  const [tokenStatus, setTokenStatus] = useState({
    isPremium: false,
    balance: 0,
    loading: true,
    error: null
  });

  const [lastChecked, setLastChecked] = useState(null);

  /**
   * Check token balance and update status
   */
  const checkTokenStatus = useCallback(async () => {
    if (!walletAddress) {
      setTokenStatus({
        isPremium: false,
        balance: 0,
        loading: false,
        error: null
      });
      return;
    }

    setTokenStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await znoozinuTokenService.checkPremiumStatus(walletAddress);
      
      setTokenStatus({
        isPremium: result.isPremium,
        balance: result.balance,
        loading: false,
        error: result.error || null
      });
      
      setLastChecked(new Date());
      
    } catch (error) {
      console.error('Error checking ZNOOZINU token status:', error);
      setTokenStatus({
        isPremium: false,
        balance: 0,
        loading: false,
        error: error.message
      });
    }
  }, [walletAddress]);

  /**
   * Force refresh token status (bypass cache)
   */
  const refreshTokenStatus = useCallback(() => {
    znoozinuTokenService.clearCache();
    checkTokenStatus();
  }, [checkTokenStatus]);

  // Check token status when wallet address changes
  useEffect(() => {
    checkTokenStatus();
  }, [checkTokenStatus]);

  // Auto-refresh every 10 minutes (reduced frequency to prevent excessive polling)
  useEffect(() => {
    if (!walletAddress) return;

    const interval = setInterval(() => {
      // Only refresh if we don't have recent cached data
      const service = znoozinuTokenService;
      const cached = service.getCachedBalance(walletAddress);
      if (cached === null) {
        // Auto-refreshing ZNOOZINU token status (cache expired)
        checkTokenStatus();
      } else {
        // Skipping token refresh - using cached balance
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [walletAddress, checkTokenStatus]);

  /**
   * Get premium feature requirements message
   */
  const getPremiumRequirement = () => {
    const required = znoozinuTokenService.premiumThreshold;
    const current = tokenStatus.balance;
    const needed = Math.max(0, required - current);
    
    if (tokenStatus.isPremium) {
      return `✨ Premium Active! You hold ${current.toLocaleString()} ZNOOZINU tokens.`;
    }
    
    return `🪙 Hold ${required.toLocaleString()} ZNOOZINU tokens for premium features. You need ${needed.toLocaleString()} more tokens.`;
  };

  /**
   * Get premium features list
   */
  const getPremiumFeatures = () => {
    return znoozinuTokenService.getPremiumFeatures();
  };

  /**
   * Check if specific feature is available
   */
  const hasFeature = (feature) => {
    switch (feature) {
      case 'image_upload':
      case 'gif_upload':
      case 'extended_character_limit':
      case 'custom_themes':
      case 'pin_beams':
      case 'advanced_search':
      case 'trending_priority':
      case 'custom_emoji':
      case 'beam_analytics':
        return tokenStatus.isPremium;
      default:
        return true; // Basic features available to all
    }
  };

  /**
   * Get character limit based on token status
   */
  const getCharacterLimit = () => {
    return tokenStatus.isPremium ? 500 : 280;
  };

  /**
   * Get max images per beam based on token status
   */
  const getMaxImages = () => {
    return tokenStatus.isPremium ? 4 : 0;
  };

  return {
    // Token status
    isPremium: tokenStatus.isPremium,
    balance: tokenStatus.balance,
    loading: tokenStatus.loading,
    error: tokenStatus.error,
    lastChecked,
    
    // Utility functions
    checkTokenStatus,
    refreshTokenStatus,
    getPremiumRequirement,
    getPremiumFeatures,
    hasFeature,
    getCharacterLimit,
    getMaxImages,
    
    // Premium thresholds
    premiumThreshold: znoozinuTokenService.premiumThreshold
  };
};

export default useZnoozinuToken;
